import { useState, useMemo } from "react";
import { toast } from "react-hot-toast";
import { useQuery } from "@tanstack/react-query";
import { planService } from "../../../../services/planService";
import type { Subscription } from "../../../../services/planService";
import { permissionsService, type AvailablePlan } from "../../../../services/permissionsService";
import { PricingPlan, DEV_STORE_PLAN, formatRevenueFee } from "../../../../constants/pricing";
import { isSamePlan, isStarterPlan, getPlanDisplayName } from "../../../../utils/planUtils";
import { useBrand } from "../../../../contexts/BrandContext/hooks";
import { redirectToShopify } from "../../../../lib/shopifyRedirect";
import { enterprisePlanService } from "../../../../services/enterprisePlanService";
import { EnterprisePlanCard } from "../../../../components/pricing/EnterprisePlanCard";
import { EnterpriseContactModal } from "../../../../components/pricing/EnterpriseContactModal";

const PricingCardSkeleton = ({ isPopular }: { isPopular?: boolean }) => (
  <div className="relative p-4 sm:p-6 rounded-xl border-2 border-border animate-pulse flex flex-col h-full">
    {isPopular && (
      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-6 py-1 bg-muted rounded-full">
        <div className="h-4 w-20"></div>
      </div>
    )}
    <div className="mb-4 sm:mb-6">
      {/* Price skeleton */}
      <div className="flex items-baseline">
        <div className="h-8 w-16 bg-muted rounded-lg"></div>
        <div className="h-4 w-8 bg-muted rounded-lg ml-1"></div>
      </div>
      {/* Name skeleton */}
      <div className="h-6 w-24 bg-muted rounded-lg mt-3 mb-2"></div>
      {/* Description skeleton */}
      <div className="space-y-2">
        <div className="h-4 w-full bg-muted rounded-lg"></div>
        <div className="h-4 w-4/5 bg-muted rounded-lg"></div>
      </div>
    </div>

    {/* Features skeleton */}
    <div className="space-y-3 mb-6 flex-grow">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-start gap-2">
          <div className="w-5 h-5 bg-muted rounded-full flex-shrink-0 mt-0.5"></div>
          <div className="h-4 w-full bg-muted rounded-lg"></div>
        </div>
      ))}
    </div>

    {/* Button skeleton */}
    <div className="h-11 w-full bg-muted rounded-lg"></div>
  </div>
);

export function PricingDetails() {
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);
  const { brandDetails } = useBrand();

  // Fetch current subscription
  const {
    data: subscription,
    isLoading: isLoadingSubscription,
    refetch,
  } = useQuery<Subscription | null>({
    queryKey: ["subscription"],
    queryFn: () => planService.getCurrentSubscription(),
  });

  // Fetch available plans from backend
  const { data: backendPlans, isLoading: isLoadingPlans } = useQuery<AvailablePlan[]>({
    queryKey: ["available-plans"],
    queryFn: () => permissionsService.getAvailablePlans(),
  });

  // Status of this store's Enterprise (custom plan) request, if any
  const { data: enterpriseRequest } = useQuery({
    queryKey: ["enterprise-plan-request"],
    queryFn: () => enterprisePlanService.getMyRequest(),
  });

  // Dev stores only ever see the Dev Store plan, so Enterprise stays hidden there.
  // Decided from the plans the backend returned, since brandDetails.isDevStore is
  // not always set for a dev store session.
  const showEnterpriseTier =
    !brandDetails?.isDevStore &&
    !backendPlans?.some((p) => p.name === "Dev Store");
  const hasOpenEnterpriseRequest = enterpriseRequest?.hasOpenRequest ?? false;

  // Only consider a plan as current if subscription exists and is active
  const currentPlanName = subscription?.data?.active ? subscription.name : null;
  const currentPlanData = subscription?.data?.plan;

  // Convert backend plans to pricing plans format and include current "Old" plan if applicable
  const pricingPlans: PricingPlan[] = useMemo(() => {
    // Dev stores only see the Dev Store plan (handled by backend, but show hardcoded version)
    if (brandDetails?.isDevStore) {
      return [DEV_STORE_PLAN];
    }

    if (!backendPlans) return [];
    
    // Backend already filters out "Dev Store" for non-dev stores
    const activePlans = backendPlans.map((plan, index) => {
      // Use plan descriptions - they are the source of truth for what to display
      // Order 0 is used as the plan description (shown below plan name) AND in features list
      // Order 1+ are also used as features in the list
      const allDescriptions = plan.descriptions || [];
      const planDescription = allDescriptions.find(d => d.order === 0)?.description || '';
      const featureDescriptions = allDescriptions
        .filter(d => d.order >= 0) // Include order 0 in features list
        .sort((a, b) => a.order - b.order)
        .map(desc => ({
          name: desc.description,
        }));

      // Included AI search quota comes from the backend plan config
      if (plan.aiSearchQuestions) {
        featureDescriptions.push({
          name: `${plan.aiSearchQuestions} Social searches per month`,
        });
      }

      return {
        id: plan.id,
        name: plan.name,
        price: plan.price.toString(),
        revenue: formatRevenueFee(plan.commissionRate),
        description: planDescription,
        isPopular: index === backendPlans.length - 1, // Mark last plan as popular
        features: featureDescriptions,
      };
    });

    // If user has a current plan that's not in the active plans (e.g., "Starter Old")
    // Add it to the beginning of the list so they can see their current plan
    if (currentPlanName && currentPlanData && !activePlans.some(p => p.name === currentPlanName)) {
      const currentPlanCard: PricingPlan = {
        id: currentPlanData.id,
        name: currentPlanName,
        price: currentPlanData.price.toString(),
        revenue: 'Legacy Plan',
        isPopular: false,
        features: [
          { name: 'All features included', description: 'Full platform access' },
          { name: 'Unlimited limits', description: 'No restrictions' },
          { name: 'Legacy pricing', description: 'Original commission rates' },
        ],
      };
      
      return [currentPlanCard, ...activePlans];
    }

    return activePlans;
  }, [backendPlans, currentPlanName, currentPlanData, brandDetails?.isDevStore]);

  // Check admin invite status
  const { data: adminInviteStatus } = useQuery({
    queryKey: ["adminInviteStatus"],
    queryFn: () => planService.getAdminInviteStatus(),
  });

  // Grace status from DB (covers admin invite or landing trial)
  const { data: graceStatus } = useQuery({
    queryKey: ["thirtyDayGraceStatus"],
    queryFn: () => planService.getGraceStatus(),
  });

  const showThirtyDayBanner = graceStatus?.hasThirtyDayGrace ?? (adminInviteStatus?.invitedByAdmin ?? false);

  const handleSubscribe = async (planName: string) => {
    if (planName === currentPlanName) {
      return;
    }

    try {
      setIsLoading(planName);
      const checkoutUrl = await planService.subscribeToPlan(planName);

      if (checkoutUrl) {
        redirectToShopify(checkoutUrl);
      } else {
        toast.error("Failed to generate checkout URL");
      }
    } catch {
      toast.error("Failed to initiate subscription. Please try again.");
    } finally {
      setIsLoading(null);
    }
  };

  const handleCancelSubscription = async () => {
    if (!currentPlanName) return;

    const confirmed = window.confirm(
      "Are you sure you want to cancel your subscription? Your plan will be downgraded to Starter at the end of your current billing period."
    );

    if (!confirmed) return;

    try {
      setIsCancelling(true);
      await planService.cancelSubscription();
      await refetch();
      toast.success("Your subscription has been cancelled successfully");
    } catch {
      toast.error("Failed to cancel subscription. Please try again.");
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 ">
      <div className="text-center mb-8 sm:mb-12">
        {showThirtyDayBanner && (
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-50 text-brand-700 border border-brand-200">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
            <span className="text-sm sm:text-base">Enjoy 30 days free usage charges.</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
        {isLoadingSubscription || isLoadingPlans ? (
          <>
            <PricingCardSkeleton />
            <PricingCardSkeleton isPopular />
            <PricingCardSkeleton />
            <PricingCardSkeleton />
          </>
        ) : (
          pricingPlans.map((plan) => {
            // Use exact matching for current plan check (not normalized)
            const isCurrentPlan = plan.name === currentPlanName;

            // Get the indices for comparing plan levels (normalize names for comparison)
            const currentPlanIndex = pricingPlans.findIndex(
              (p) => isSamePlan(p.name, currentPlanName)
            );
            const targetPlanIndex = pricingPlans.findIndex(
              (p) => p.name === plan.name
            );

            // Can upgrade only if:
            // 1. No current plan (new user) OR
            // 2. Target plan index is higher than current plan index
            const canUpgrade =
              !currentPlanName || targetPlanIndex > currentPlanIndex;

            // Can downgrade only if:
            // 1. Has a current plan AND
            // 2. Target plan index is lower than current plan index
            const canDowngrade =
              currentPlanName && targetPlanIndex < currentPlanIndex;

            return (
              <div
                key={plan.name}
                className={`relative p-6 sm:p-8 rounded-xl border-2 ${
                  isCurrentPlan
                    ? "border-brand-500 bg-brand-50"
                    : plan.isPopular
                    ? "border-brand-200 shadow-lg"
                    : "border-border"
                } flex flex-col h-full transform transition-transform duration-300 hover:scale-105`}
              >
                {plan.isPopular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-3 py-1 bg-brand-600 text-white text-sm rounded-full font-medium">
                    most popular
                  </div>
                )}
                <div className="mb-6 sm:mb-8">
                  <h3 className="text-2xl sm:text-3xl font-bold">
                    ${plan.price}
                    <span className="text-base font-normal text-muted-foreground">
                      /mo
                    </span>
                  </h3>
                  <p className="text-lg sm:text-xl font-semibold mt-3">
                    {plan.name}
                  </p>
                  <p className="mt-2 text-sm font-medium text-brand-600">
                    {plan.revenue}
                  </p>
                  {isCurrentPlan && (
                    <div className="mt-3 space-y-2">
                      <span className="inline-block px-3 py-1 bg-brand-100 text-brand-600 rounded-full text-sm">
                        {getPlanDisplayName(currentPlanName)}
                      </span>
                      {!isStarterPlan(currentPlanName) && (
                        <button
                          onClick={handleCancelSubscription}
                          disabled={isCancelling}
                          className="block w-full mt-2 px-3 py-1.5 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-card hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 transition-colors duration-200"
                        >
                          {isCancelling ? (
                            <span className="flex items-center justify-center gap-2">
                              <svg
                                className="animate-spin h-4 w-4"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                />
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                              </svg>
                              Cancelling...
                            </span>
                          ) : (
                            "Cancel Subscription"
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Features List with better spacing */}
                <div className="border-t border-border pt-6 mb-6 flex-grow">
                    <ul className="space-y-3">
                    {plan.features.map((feature, index) => (
                      <li
                        key={index}
                        className="flex items-start gap-3 text-sm sm:text-base"
                      >
                        <svg
                          className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <div className="flex-1">
                          <span className="text-muted-foreground leading-relaxed">{feature.name}</span>
                          {feature.description && (
                            <p className="text-xs text-muted-foreground mt-0.5">{feature.description}</p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  onClick={() => handleSubscribe(plan.name)}
                  className={`w-full py-2.5 sm:py-3 px-4 rounded-lg font-medium text-sm sm:text-base transition-colors duration-200 ${
                    isCurrentPlan
                      ? "bg-muted text-muted-foreground cursor-not-allowed"
                      : !canUpgrade && !canDowngrade
                      ? "bg-muted text-muted-foreground cursor-not-allowed"
                      : "bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-70"
                  }`}
                  disabled={
                    isCurrentPlan ||
                    (!canUpgrade && !canDowngrade) ||
                    isLoading === plan.name ||
                    isLoadingSubscription
                  }
                >
                  {isLoading === plan.name ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Processing...
                    </span>
                  ) : isCurrentPlan ? (
                    "Current Plan"
                  ) : !canUpgrade && !canDowngrade ? (
                    isStarterPlan(plan.name) ? (
                      "Default Free Plan"
                    ) : (
                      "Not Available"
                    )
                  ) : canUpgrade ? (
                    !currentPlanName ? (
                      "Activate Plan"
                    ) : (
                      `Upgrade to ${plan.name}`
                    )
                  ) : (
                    `Downgrade to ${plan.name}`
                  )}
                </button>
              </div>
            );
          })
        )}

        {showEnterpriseTier && !isLoadingSubscription && !isLoadingPlans && (
          <EnterprisePlanCard
            onContact={() => setShowEnterpriseModal(true)}
            hasOpenRequest={hasOpenEnterpriseRequest}
            className="p-6 sm:p-8"
          />
        )}
      </div>

      {/* Enterprise "Get in touch" form */}
      <EnterpriseContactModal
        isOpen={showEnterpriseModal}
        onClose={() => setShowEnterpriseModal(false)}
        hasOpenRequest={hasOpenEnterpriseRequest}
        defaultValues={{
          contactName:
            enterpriseRequest?.request?.contactName ||
            [brandDetails?.firstName, brandDetails?.lastName]
              .filter(Boolean)
              .join(" "),
          email: enterpriseRequest?.request?.email || brandDetails?.email,
          companyName: brandDetails?.companyName ?? undefined,
        }}
      />
    </div>
  );
}
